import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import Button from '../components/ui/Button';

export default function Onboarding() {
  const navigate = useNavigate();
  const { setCurrentUser } = useEmberStore();
  const [smokerType, setSmokerType] = useState<'pipe' | 'cigar' | 'both' | null>(null);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCancel() {
    try {
      await supabase.auth.signOut();
    } finally {
      navigate('/login', { replace: true });
    }
  }

  async function handleContinue() {
    if (!smokerType || !username.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const {
        data: { user }
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data, error: dbError } = await supabase
        .from('profiles')
        .insert({
          id: user.id,
          username: username.trim(),
          smoker_type: smokerType,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      setCurrentUser(data);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', padding: '2rem 1rem', overflowY: 'auto' }}>
      <div className="container" style={{ maxWidth: '600px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button
            onClick={handleCancel}
            style={{
              padding: '0.4rem 0.75rem',
              borderRadius: '6px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-card)',
              color: 'var(--color-accent)',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            Back to Login
          </button>
        </div>

        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Welcome to Ember</h1>
        <p style={{ color: '#d3de5e', marginBottom: '2rem' }}>
          Tell us about your smoking style
        </p>

        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Username
          </label>
          <input
            type="text"
            placeholder="Enter your username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '1rem', fontWeight: '500' }}>
            What do you smoke?
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {(['pipe', 'cigar', 'both'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setSmokerType(type)}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '999px',
                  border: smokerType === type ? '2px solid #ff9500' : '2px solid #6b4423',
                  backgroundColor: smokerType === type ? '#5a3310' : 'transparent',
                  color: smokerType === type ? '#ff9500' : '#ffffff',
                  fontWeight: '500',
                }}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div style={{ color: '#ef4444', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <Button
          onClick={handleContinue}
          variant="primary"
          loading={loading}
          disabled={!smokerType || !username.trim()}
          fullWidth
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
